import {
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  viewChild
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { TemplateInput, parseListInput } from '@core/filters/path-filter/templates';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import {
  datetimeLocalToIso,
  isoToDatetimeLocal
} from '@core/utils/recipe-parameter-schema.utils';
import { sameJson } from '@core/utils/shared-utils';

import { EntityAutoCompleteComponent } from '@shared/components/row-edition-form/entity-auto-complete/entity-auto-complete.component';
import { SelectValue } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';
import { AvailableLanguages } from '@shared/types/translation.type';

import { closedValueItems } from '../path-filter-labels';
import { DurationInputComponent } from '../value/duration-input/duration-input.component';

export type TemplateValue = string | string[];

/**
 * The value of one template parameter, in its wire form, through the control its slot takes: an
 * entity picker for a uid, the field's values for a closed field, a typed input otherwise.
 */
@Component({
  selector: 'app-template-value-input',
  imports: [
    TranslatePipe,
    EntityAutoCompleteComponent,
    SelectComponent,
    DurationInputComponent
  ],
  templateUrl: './template-value-input.component.html',
  styles: `
    :host {
      display: block;
      min-width: 0;
    }
  `
})
export class TemplateValueInputComponent {
  readonly parameter = input.required<TemplateInput>();
  readonly value = input<TemplateValue | undefined>(undefined);
  /** On the control a `<label for>` names; the duration input takes `label` instead. */
  readonly inputId = input<string | undefined>(undefined);
  readonly label = input('');

  readonly valueChange = output<TemplateValue>();

  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly lang = computed(() => this.currentLang() as AvailableLanguages);
  private readonly picker = viewChild(EntityAutoCompleteComponent);

  /** Kept raw: re-printing the parsed list on each keystroke would eat a trailing comma. */
  protected readonly listText = linkedSignal<TemplateValue | undefined, string>({
    source: () => this.value(),
    computation: (value, previous) => {
      const items = Array.isArray(value) ? value : [];
      return previous && sameJson(parseListInput(previous.value), items)
        ? previous.value
        : items.join(', ');
    }
  });

  protected readonly text = computed(() => {
    const value = this.value();
    return typeof value === 'string' ? value : '';
  });

  protected readonly closedItems = computed(() => {
    const value = this.value() ?? [];
    return closedValueItems(
      this.parameter().closed ?? undefined,
      Array.isArray(value) ? value : [value],
      this.lang()
    );
  });

  protected readonly closedValue = computed<SelectValue>(() => {
    const value = this.value();
    if (this.parameter().kind === 'list') return Array.isArray(value) ? value : [];
    return typeof value === 'string' && value !== '' ? value : null;
  });

  protected readonly localDateTime = computed(() => isoToDatetimeLocal(this.text()));

  /** The entity list is open on a typed term, which Enter must not apply half-done. */
  searching(): boolean {
    return this.picker()?.searching() ?? false;
  }

  /** Whether `target` is the picked entity's chip, where the focus lands after a pick. */
  isValueChip(target: EventTarget | null): boolean {
    return this.picker()?.isValueChip(target) ?? false;
  }

  protected onReference(value: string | string[] | null): void {
    this.valueChange.emit(value ?? '');
  }

  protected onClosed(value: SelectValue): void {
    if (Array.isArray(value)) this.valueChange.emit(value.map(String));
    else this.valueChange.emit(value === null ? '' : String(value));
  }

  protected onDateTime(local: string): void {
    this.valueChange.emit(datetimeLocalToIso(local));
  }

  protected onList(raw: string): void {
    this.listText.set(raw);
    this.valueChange.emit(parseListInput(raw));
  }
}
